import React, { useState, useContext, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import { debounce } from 'lodash';
import { UserContext } from '../../../context/UserContext';
import StepIndicator from '../../basic/MyStepIndicator';
import MyButton from '../../basic/MyButton';
import MyTextInput from '../../basic/MyTextInput';
import MyDropdownWeekdays from '../../basic/MyDropdownWeekdays';
import MyLoading from '../../basic/MyLoading';
import MyCustomIcon from '../../basic/MyCustomIcon';
import { checkEmailExistence, checkUsernameExistence } from '../../../api';
import './ModalAddFamilyMember.css';

Modal.setAppElement('#root');

function AddFamilyMemberModal({ isOpen, onClose, onAddMember }) {
    const { user } = useContext(UserContext);
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        role: '',
        display_name: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        family_id: user.details.family_id,
        allowanceAmount: '',
        allowanceDay: 'Monday',
        initialDeposit: '',
        depositReason: 'First Deposit'
    });
    const [error, setError] = useState('');
    const [emailExists, setEmailExists] = useState(false);
    const [usernameExists, setUsernameExists] = useState(false);
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [emailMessage, setEmailMessage] = useState('');
    const [usernameMessage, setUsernameMessage] = useState('');

    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            setFormData({
                role: '',
                display_name: '',
                email: '',
                username: '',
                password: '',
                confirmPassword: '',
                family_id: user.details.family_id,
                allowanceAmount: '',
                allowanceDay: 'Monday',
                initialDeposit: '',
                depositReason: 'First Deposit'
            });
            setError('');
            setEmailExists(false);
            setUsernameExists(false);
            setIsCheckingEmail(false);
            setIsCheckingUsername(false);
            setEmailMessage('');
            setUsernameMessage('');
        }
    }, [isOpen, user.details.family_id]);

    const parentSteps = [
        "Display Name",
        "Login Details"
    ];

    const childSteps = [
        "Display Name",
        "Login Details",
        "Set Allowance",
        "Initial Deposit"
    ];

    const steps = formData.role === 'parent' ? parentSteps : childSteps;

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const MIN_USERNAME_LENGTH = 4;

    const sequentialEmailCheck = useCallback(
        debounce(async (email) => {
            setIsCheckingEmail(false);
            setEmailExists(false);
            setEmailMessage('');
    
            if (email.trim() === '') {
                return;
            }
    
            if (!EMAIL_REGEX.test(email)) {
                setEmailMessage('Please enter a valid email address');
                setEmailExists(true);
                return;
            }
    
            setIsCheckingEmail(true);
            setEmailMessage('Checking email...');
            
            try {
                const { exists } = await checkEmailExistence(email);
                setEmailExists(exists);
                setEmailMessage(exists 
                    ? 'This Email is already registered, try another email address.'
                    : 'This Email is available.');
            } catch (error) {
                console.error('Error checking email existence:', error);
                setEmailMessage('Error checking email. Please try again.');
                setEmailExists(true);
            } finally {
                setIsCheckingEmail(false);
            }
        }, 300),
        []
    );

    const sequentialUsernameCheck = useCallback(
        debounce(async (username) => {
            // Check for spaces first
            if (username.includes(' ')) {
                setUsernameMessage('Usernames cannot have spaces');
                setUsernameExists(true);
                return;
            }
    
            if (username.length >= MIN_USERNAME_LENGTH) {
                setIsCheckingUsername(true);
                setUsernameMessage('Checking username...', username);
                try {
                    const response = await checkUsernameExistence(username);
                    
                    // Check if the response has the expected structure
                    if (response && typeof response.exists === 'boolean') {
                        setUsernameExists(response.exists);
                        setUsernameMessage(response.exists
                            ? 'This username is taken, create a unique username.'
                            : 'Username is available.');
                    } else {
                        // Handle unexpected response format
                        console.error('Unexpected response format:', response);
                        setUsernameMessage('Error checking username. Please try again.', username);
                        setUsernameExists(true); // Treat as existing to prevent form submission
                    }
                } catch (error) {
                    console.error('Error checking username existence:', error);
                    setUsernameMessage('Error checking username. Please try again.', username);
                    setUsernameExists(true); // Treat as existing to prevent form submission
                } finally {
                    setIsCheckingUsername(false);
                }
            } else {
                setUsernameMessage(`Username must be at least ${MIN_USERNAME_LENGTH} characters long.`);
                setUsernameExists(true);
            }
        }, 300),
        []
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'email') {
            sequentialEmailCheck(value);
        } else if (name === 'username') {
            sequentialUsernameCheck(value);
        }
    };

    const handleNext = (e) => {
        e.preventDefault();
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = (e) => {
        e.preventDefault();
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        const newMemberData = {
            display_name: formData.display_name,
            email: formData.email,
            username: formData.username,
            password: formData.password,
            family_id: formData.family_id,
            role: formData.role
        };

        if (formData.role === 'child') {
            newMemberData.allowanceSettings = {
                amount: parseFloat(formData.allowanceAmount),
                day: formData.allowanceDay
            };
            newMemberData.initialDeposit = {
                amount: parseFloat(formData.initialDeposit),
                reason: formData.depositReason
            };
        }

        console.log('Submitting new member data from modal:', newMemberData);
        onAddMember(newMemberData);
        onClose();
    };

    const handleContentClick = (e) => {
        e.stopPropagation();
    };

    const isStepValid = () => {
        switch (currentStep) {
            case 0:
                return formData.display_name.trim() !== '';
            case 1:
                return formData.username.trim() !== '' && 
                       formData.password.trim() !== '' && 
                       formData.confirmPassword.trim() !== '' &&
                       formData.password === formData.confirmPassword &&
                       !emailExists &&
                       !usernameExists &&
                       !isCheckingEmail &&
                       !isCheckingUsername;
            case 2:
                return formData.allowanceAmount.trim() !== '' && 
                       formData.allowanceDay !== '';
            case 3:
                return formData.initialDeposit.trim() !== '' && 
                       formData.depositReason.trim() !== '';
            default:
                return true;
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (isStepValid()) {
                if (currentStep < steps.length - 1) {
                    handleNext(e);
                } else {
                    handleSubmit(e);
                }
            }
        }
    };

    const renderStepContent = () => {
        if (!formData.role) {
            return (
                <>
                    <h3>Select Role</h3>
                    <div className="role-selection-buttons">
                        <MyButton 
                            text="Parent / Guardian" 
                            onClick={() => { setFormData(prev => ({ ...prev, role: 'parent' })); setCurrentStep(0); }}
                            className="role-button"
                        />
                        <MyButton 
                            text="Child" 
                            onClick={() => { setFormData(prev => ({ ...prev, role: 'child' })); setCurrentStep(0); }}
                            className="role-button"
                        />
                    </div>
                </>
            );
        }

        switch (currentStep) {
            case 0:
                return (
                    <>
                        <h3>Enter Display Name</h3>
                        <MyTextInput
                            label="Display Name"
                            name="display_name"
                            value={formData.display_name}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            required
                        />
                    </>
                );
            case 1:
                return (
                    <>
                        <h3>Enter Login Details</h3>
                        <h5>We've designed our family user setup with flexibility and privacy in mind, allowing you to add family members without requiring email addresses for everyone. This approach lets you manage your family's users while respecting individual privacy preferences.</h5>
                        <h5>When adding a new user, assign a username and password for them. These credentials can be changed after the user is created.</h5>
                        <h5>Once created, users can change their own password using the 'Forgot Password' feature available on the login page.</h5>

                        <div className="input-group">
                            <MyTextInput
                                label="Email (optional)"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                            />
                            {isCheckingEmail && (
                                <div className="indicator-wrapper">
                                    <MyLoading size="small" />
                                </div>
                            )}
                            {emailMessage && !isCheckingEmail && (
                                <div className="indicator-wrapper">
                                    <div className="indicator-content">
                                        {emailExists ? (
                                            <span className="error-message">{emailMessage}</span>
                                        ) : (
                                            <>
                                                <MyCustomIcon type="check" color="green" />
                                                <span className="success-text">{emailMessage}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="input-group">
                            <MyTextInput
                                label="Username"
                                name="username"
                                value={formData.username}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                required
                            />
                            {isCheckingUsername && (
                                <div className="indicator-wrapper">
                                    <MyLoading size="small" />
                                </div>
                            )}
                            {usernameMessage && !isCheckingUsername && (
                                <div className="indicator-wrapper">
                                    <div className="indicator-content">
                                        {usernameExists ? (
                                            <span className="error-message">{usernameMessage}</span>
                                        ) : (
                                            <>
                                                <MyCustomIcon type="check" color="green" />
                                                <span className="success-text">{usernameMessage}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <MyTextInput
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <MyTextInput
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            required
                        />
                        {formData.password !== formData.confirmPassword && 
                            <div className="error-message">Passwords do not match</div>
                        }
                    </>
                );
            case 2:
                return formData.role === 'child' ? (
                    <>
                        <h3>Set Weekly Allowance</h3>
                        <MyTextInput
                            label="Allowance Amount"
                            name="allowanceAmount"
                            type="number"
                            value={formData.allowanceAmount}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <MyDropdownWeekdays
                            label="Allowance Day"
                            selectedDay={formData.allowanceDay}
                            onChange={(e) => setFormData(prev => ({ ...prev, allowanceDay: e.target.value }))}
                            required
                        />
                    </>
                ) : null;
            case 3:
                return formData.role === 'child' ? (
                    <>
                        <h3>Initial Deposit</h3>
                        <p>Kick off  <strong>{formData.display_name}</strong>'s savings adventure with their first deposit!</p>
                        <MyTextInput
                            label="Deposit Amount"
                            name="initialDeposit"
                            type="number"
                            value={formData.initialDeposit}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            required
                        />
                        <MyTextInput
                            label="Deposit Reason"
                            name="depositReason"
                            value={formData.depositReason}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            required
                        />
                    </>
                ) : null;
            default:
                return null;
        }
    };

    const getModalTitle = () => {
        if (!formData.role) return "Add Family Member";
        return formData.role === 'parent' ? "Add Parent / Guardian User" : "Add Child User";
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={true}
            contentLabel="Add Family Member"
            className="add-family-member-modal"
            overlayClassName="add-family-member-modal-overlay"
        >
            <div>
                <h2>{getModalTitle()}</h2>
                {error && <div className="error-message">{error}</div>}
                {formData.role && <StepIndicator steps={steps} currentStep={currentStep} />}
                <div>
                    {renderStepContent()}
                    <div className="modal-actions">
                        {currentStep > 0 && <MyButton text="Back" onClick={handleBack} />}
                        {formData.role && currentStep < steps.length - 1 && 
                            <MyButton 
                                text="Next" 
                                onClick={handleNext} 
                                disabled={!isStepValid()}
                            />
                        }
                        {formData.role && currentStep === steps.length - 1 && 
                            <MyButton 
                                text="Submit" 
                                onClick={handleSubmit}
                                disabled={!isStepValid()}
                            />
                        }
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default AddFamilyMemberModal;